/**
 * The operator manual's eight exception categories (AIVA-9193): capitalised
 * words that are not defined terms, so the defined-terms checks must not flag
 * them or spend a model call deciding that.
 *
 *   place names · company names · corporate titles · document titles ·
 *   government bodies · statute names · the words Section and Exhibit ·
 *   terms defined in related agreements
 *
 * Before this, every such phrase that was quoted and recurred became a
 * used_not_defined candidate, and the backend made one model call per
 * candidate only to be told "a place name, not a defined term". Filtering here,
 * in the sweep, settles them before any model call.
 *
 * A category is RECONSIDERED for a document that itself defines a member of it
 * (`"Director" means …`, `"Escrow Agreement" means …`): the drafter is treating
 * that kind of word as a defined term there, so the category's other members go
 * to the model as usual instead of being set aside.
 *
 * Deterministic and deliberately conservative: a phrase that matches no
 * category is left for the model, never dropped. Pure, so every category is
 * testable without an Office host.
 */

export type ExceptionCategory =
  | "place"
  | "company"
  | "corporate_title"
  | "document_title"
  | "government_body"
  | "statute"
  | "section_exhibit"
  | "related_agreement";

const words = (term: string): string[] => term.trim().split(/\s+/).filter(Boolean);
const isMultiWord = (term: string): boolean => words(term).length >= 2;
const lastWord = (term: string): string => {
  const all = words(term);
  return all[all.length - 1] ?? "";
};
const tail = (term: string): string => lastWord(term).toLowerCase();
const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

// --- the words Section and Exhibit ----------------------------------------

const SECTION_EXHIBIT = /^(?:sections?|exhibits?)(?:\s|$)/i;

// --- statute names --------------------------------------------------------

/** `Companies Act`, `Data Protection Regulations`, `Insolvency Rules` — the
 *  year sits outside the quotes, so the term ends on the instrument word. */
const STATUTE_TAIL = new Set(["act", "acts", "regulation", "regulations", "directive", "statute", "statutes", "ordinance", "treaty", "convention"]);
const STATUTE_CODE = /^(?:internal revenue|bankruptcy|uniform commercial|civil|penal|criminal|tax|labour|labor) code$/i;
/** "DPA" is left out: in a contract it is usually the data processing agreement. */
const STATUTE_ACRONYMS = new Set(["gdpr", "uk gdpr", "eu gdpr", "ccpa", "cpra", "hipaa", "fcpa", "erisa", "tupe", "sox", "fsma"]);

// --- government bodies ----------------------------------------------------

const GOVERNMENT_TAIL = new Set(["commission", "authority", "agency", "department", "ministry", "court", "courts", "tribunal", "bureau", "parliament", "government", "treasury", "regulator", "congress", "senate"]);
const GOVERNMENT_ACRONYMS = new Set([
  "sec", "fca", "pra", "hmrc", "irs", "cma", "ftc", "doj", "ico", "ofac", "finra", "cftc", "esma", "eba", "ecb", "fed", "ofcom", "ofgem", "ofwat", "sfo", "nca", "ec",
]);

// --- company names --------------------------------------------------------

/** Legal-form suffixes. "Company" and "Group" are left out on purpose: "the
 *  Company" and "Group Company" are among the commonest defined terms. */
const COMPANY_SUFFIX = new Set([
  "limited", "ltd", "llc", "llp", "lp", "plc", "inc", "incorporated", "corp", "corporation", "gmbh", "ag", "sa", "sas", "sarl", "nv", "bv", "pty", "spa", "srl", "kg", "oy", "ab",
]);
/** `Northbridge Analytics, a company incorporated in …` — the sentence says
 *  the name is a company's. */
const COMPANY_LEAD = /^"?\)?\s*(?:,\s*)?an?\s+/i;
const COMPANY_NOUN = /^(?:company|corporation|partnership|limited liability company|limited partnership|body corporate)\b/i;
const PLAIN_WORD = /^[a-z-]+$/i;

/** What follows the name reads "a/an <up to four words> company|corporation|…".
 *  Walked word by word rather than one regex, which backtracked badly. */
function namesACompany(after: string): boolean {
  const lead = COMPANY_LEAD.exec(after);
  if (!lead) return false;
  const rest = words(after.slice(lead[0].length));
  for (let skip = 0; skip <= 4 && skip < rest.length; skip++) {
    if (COMPANY_NOUN.test(rest.slice(skip).join(" "))) return true;
    if (!PLAIN_WORD.test(rest[skip] ?? "")) return false;
  }
  return false;
}

// --- corporate titles -----------------------------------------------------

const TITLE_HEAD = new Set([
  "officer", "director", "chairman", "chairwoman", "chairperson", "chair", "president", "secretary", "treasurer", "counsel", "controller", "comptroller",
]);
const TITLE_MODIFIER = new Set([
  "senior", "executive", "deputy", "assistant", "associate", "vice", "managing", "non-executive", "group", "regional", "general", "company",
]);

/** `Chief Executive Officer`, `Senior Vice President`, `Non Executive Director`:
 *  a title word led only by modifiers, "Chief <any word>" or "Non Executive". */
function isCorporateTitle(term: string): boolean {
  const all = words(term.toLowerCase());
  const head = all.pop();
  if (!head || !TITLE_HEAD.has(head)) return false;
  for (let i = 0; i < all.length; i++) {
    const word = all[i] ?? "";
    const next = all[i + 1];
    if ((word === "chief" && next !== undefined) || (word === "non" && next === "executive")) {
      i++;
    } else if (!TITLE_MODIFIER.has(word)) {
      return false;
    }
  }
  return true;
}
const CORPORATE_ACRONYMS = new Set(["ceo", "cfo", "coo", "cto", "cio", "ciso", "clo", "cmo", "cro", "gc", "md"]);

// --- document titles ------------------------------------------------------

/** The kind of instrument a title names: `Escrow Agreement`, `Disclosure
 *  Letter`. Two words at least — a lone "Agreement" is the contract itself.
 *  Plans, policies and reports are left out: an undefined "Exit Plan" is
 *  usually a deliverable the operator should hear about. */
const DOCUMENT_TAIL = new Set([
  "agreement", "agreements", "deed", "deeds", "contract", "contracts", "letter", "memorandum", "certificate", "prospectus", "charter", "instrument", "indenture", "guarantee", "lease", "licence", "license", "bylaws",
]);

// --- place names ----------------------------------------------------------

const PLACES = new Set([
  // Jurisdictions and countries that recur in contracts.
  "england", "wales", "england and wales", "scotland", "northern ireland", "ireland", "united kingdom", "great britain", "britain",
  "united states", "united states of america", "america", "canada", "mexico", "brazil", "argentina", "chile",
  "france", "germany", "spain", "portugal", "italy", "netherlands", "belgium", "luxembourg", "switzerland", "austria",
  "sweden", "norway", "denmark", "finland", "iceland", "poland", "czech republic", "hungary", "romania", "greece",
  "cyprus", "malta", "jersey", "guernsey", "isle of man", "gibraltar", "bermuda", "cayman islands", "british virgin islands",
  "european union", "european economic area", "india", "china", "japan", "korea", "south korea", "singapore", "hong kong",
  "australia", "new zealand", "south africa", "nigeria", "kenya", "egypt", "israel", "turkey", "russia", "ukraine",
  "united arab emirates", "saudi arabia", "qatar", "bahrain", "kuwait", "dubai", "abu dhabi",
  // US states commonly chosen as governing law or seat.
  "delaware", "new york", "california", "texas", "florida", "illinois", "massachusetts", "new jersey", "pennsylvania",
  "nevada", "washington", "virginia", "georgia", "ohio", "michigan", "colorado", "arizona", "north carolina", "connecticut",
  "maryland", "minnesota", "oregon", "utah", "wyoming",
  // Financial and legal centres.
  "london", "edinburgh", "glasgow", "manchester", "birmingham", "leeds", "bristol", "belfast", "cardiff", "dublin",
  "paris", "frankfurt", "berlin", "munich", "amsterdam", "brussels", "zurich", "geneva", "madrid", "milan", "stockholm",
  "boston", "chicago", "san francisco", "los angeles", "houston", "miami", "toronto", "sydney", "melbourne", "tokyo",
  "shanghai", "beijing", "mumbai", "bangalore", "chennai", "delhi", "new delhi",
]);
/** `Fenchurch Avenue`, `Boylston Street` — part of an address. */
const ADDRESS_TAIL = new Set(["street", "road", "avenue", "lane", "square", "boulevard", "terrace", "crescent", "wharf", "quay"]);

// --- terms defined in related agreements ----------------------------------

/** `"Permitted Liens" (as defined in the Credit Agreement)` — the term
 *  belongs to another document, so this one need not define it. "As defined
 *  in Section 4" points inside this document and does not count. */
function definedInRelatedAgreement(term: string, contexts: readonly string[]): boolean {
  const follower = String.raw`["”']?\)?,?\s*\(?\s*(?:each\s+|in\s+each\s+case\s+)?(?:as\s+(?:such\s+term\s+is\s+|that\s+term\s+is\s+)?defined\s+in|within\s+the\s+meaning\s+(?:given\s+to\s+it\s+)?(?:of|in))\s+(?:the\s+|that\s+|each\s+|any\s+)?(?!(?:Sections?|Clauses?|Articles?|Schedules?|Paragraphs?|Exhibits?|Annex(?:es)?|Appendi(?:x|ces)|Parts?)\b)[A-Z]`;
  const pattern = new RegExp(escapeRegExp(term) + follower);
  return contexts.some((text) => pattern.test(text.replace(/[“”]/g, '"')));
}

function isCompany(term: string, contexts: readonly string[]): boolean {
  if (isMultiWord(term) && COMPANY_SUFFIX.has(tail(term))) return true;
  const named = new RegExp(escapeRegExp(term) + "(.{0,80})");
  return contexts.some((text) => {
    const after = named.exec(text.replace(/[“”]/g, '"'))?.[1];
    return after !== undefined && namesACompany(after);
  });
}

function isPlace(term: string): boolean {
  const key = term.trim().toLowerCase();
  if (PLACES.has(key)) return true;
  // "State of Delaware", "Commonwealth of Massachusetts", "City of London".
  const of = /^(?:state|commonwealth|city|county|province|kingdom|republic) of (.+)$/.exec(key);
  if (of?.[1] && PLACES.has(of[1])) return true;
  return isMultiWord(term) && ADDRESS_TAIL.has(tail(term));
}

type CategoryTest = (term: string, contexts: readonly string[]) => boolean;

/** In the order a term is tested against them: the most specific first, so
 *  "Securities and Exchange Commission" is a government body, never a place. */
const CATEGORY_TESTS: readonly (readonly [ExceptionCategory, CategoryTest])[] = [
  ["section_exhibit", (term) => SECTION_EXHIBIT.test(term)],
  ["related_agreement", definedInRelatedAgreement],
  [
    "statute",
    (term) => STATUTE_ACRONYMS.has(term.toLowerCase()) || STATUTE_CODE.test(term) || (isMultiWord(term) && STATUTE_TAIL.has(tail(term))),
  ],
  ["government_body", (term) => GOVERNMENT_ACRONYMS.has(term.toLowerCase()) || (isMultiWord(term) && GOVERNMENT_TAIL.has(tail(term)))],
  ["company", isCompany],
  ["corporate_title", (term) => CORPORATE_ACRONYMS.has(term.toLowerCase()) || isCorporateTitle(term)],
  ["document_title", (term) => isMultiWord(term) && DOCUMENT_TAIL.has(tail(term))],
  ["place", (term) => isPlace(term)],
];

export const EXCEPTION_CATEGORIES: readonly ExceptionCategory[] = CATEGORY_TESTS.map(([category]) => category);

/**
 * Which exception category `term` falls in, or null. `contexts` are the
 * paragraphs the term appears in — what tells a company name or a term
 * borrowed from another agreement apart from a word that merely looks like one.
 */
export function exceptionCategory(term: string, contexts: readonly string[] = []): ExceptionCategory | null {
  const trimmed = term.trim();
  if (!trimmed) return null;
  return CATEGORY_TESTS.find(([, test]) => test(trimmed, contexts))?.[0] ?? null;
}

/**
 * The categories this document re-opens: each one it defines a member of.
 * `declared` is each term the document itself declares, with the paragraph
 * that declares it.
 */
export function reconsideredCategories(
  declared: readonly { term: string; context: string }[],
): Set<ExceptionCategory> {
  const reopened = new Set<ExceptionCategory>();
  for (const { term, context } of declared) {
    const category = exceptionCategory(term, [context]);
    if (category) reopened.add(category);
  }
  return reopened;
}

/** The category that sets `term` aside in this document, or null when the
 *  term goes on to the model — no category, or one the document re-opened. */
export function suppressingCategory(
  term: string,
  contexts: readonly string[],
  reconsidered: ReadonlySet<ExceptionCategory>,
): ExceptionCategory | null {
  const category = exceptionCategory(term, contexts);
  return category && !reconsidered.has(category) ? category : null;
}
